import './styles/index.scss';

import { Grid } from '@mui/material';
import { RoleInterface } from '@thro-types/access-handler';

import { Formik } from 'formik';
import { omit, pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useRoleMutation } from 'api/hooks/accessHandler';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { RoleGeneral } from './components/RoleGeneral';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  setDisabledCancel?: Function;
  role?: RoleInterface;
}

export function RoleModal(props: Props) {
  const { create, update } = useRoleMutation();

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const initialValues = () => {
    return props.role
      ? {
          ...pick(props.role, ['id', 'name']),
          entitlementGroups: props.role.entitlementGroups?.map(entitlement => entitlement.id),
          accessPermissions: props.role.accessPermissions?.map(permission => permission.id)
        }
      : {
          entitlementGroups: [],
          accessPermissions: []
        };
  };

  const handleSaveClick = async values => {
    if (create.isPending || update.isPending) {
      return;
    }
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['__typename', 'id'];
    const saveBody: any = { ...omit(values, omitFields) };
    Object.assign(saveBody, {
      entitlementGroups: saveBody.entitlementGroups.map(ent => parseInt(ent, 10)),
      accessPermissions: saveBody.accessPermissions.map(per => parseInt(per, 10))
    });

    if (values.id) {
      try {
        await update.mutateAsync({ id: values.id, data: saveBody });
        dispatch(AlertActions.success(t(translations.pages.roles.alerts.saveSuccess())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.roles.alerts.saveFailed())));
      }
    } else {
      try {
        await create.mutateAsync(saveBody);
        dispatch(AlertActions.success(t(translations.pages.roles.alerts.saveSuccess())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.roles.alerts.saveFailed())));
      }
    }
    props.closeHandler();
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const steps = ['Role Creation'];

  const RoleModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <RoleGeneral />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="role-modal">
      <Formik
        validateOnMount
        initialValues={initialValues()}
        onSubmit={values => {
          handleSaveClick(values);
        }}
      >
        <MultistepForm
          submitting={create.isPending || update.isPending}
          steps={steps}
          onCancel={props.onCancel}
          hideStepper
        >
          {activeStep => <RoleModalStep step={activeStep} />}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
